(() => {
  class PBVariantGallery {
    constructor() {
      this.colorOptionNames = ['color', 'colour', 'box color', 'linen color', 'usb color', 'finish'];
      this.globalTags = ['all', 'global', 'any'];
      this.frame = 0;
      this.pendingColor = null;
      this.pendingSelectFromUrl = false;
      this.init();
    }

    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    getRequestedColorFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return params.get('color');
    }

    getSelectedColor() {
      const requestedColor = this.getRequestedColorFromUrl();
      if (requestedColor) return requestedColor;

      const selectors = [
        'select[name*="Color" i]',
        'select[name*="Colour" i]',
        'select[name*="color" i]',
        'input[type="radio"][name*="Color" i]:checked',
        'input[type="radio"][name*="Colour" i]:checked',
        'input[type="radio"][name*="color" i]:checked',
        'variant-selects select',
        'variant-radios input[type="radio"]:checked'
      ];

      for (const selector of selectors) {
        const input = document.querySelector(selector);
        if (!input) continue;
        const name = this.normalize(input.getAttribute('name'));
        const aria = this.normalize(input.getAttribute('aria-label'));
        const id = this.normalize(input.getAttribute('id'));
        const directLooksLikeColor = this.colorOptionNames.some((optionName) =>
          name.includes(optionName) || aria.includes(optionName) || id.includes(optionName)
        );
        if (directLooksLikeColor) return input.value;

        const wrapper = input.closest('.product-form__input, fieldset, .select, variant-selects, variant-radios');
        const labelText = this.normalize(wrapper?.querySelector('legend, label, .form__label')?.textContent);
        const wrapperLooksLikeColor = this.colorOptionNames.some((optionName) => labelText.includes(optionName));
        if (wrapperLooksLikeColor) return input.value;
      }
      return null;
    }

    selectColorFromUrl() {
      const requestedColor = this.getRequestedColorFromUrl();
      if (!requestedColor) return false;
      const target = this.normalize(requestedColor);
      let changed = false;
      const controls = [...document.querySelectorAll('select'), ...document.querySelectorAll('input[type="radio"]')];

      controls.forEach((control) => {
        const name = this.normalize(control.getAttribute('name'));
        const id = this.normalize(control.getAttribute('id'));
        const aria = this.normalize(control.getAttribute('aria-label'));
        const wrapper = control.closest('.product-form__input, fieldset, .select, variant-selects, variant-radios');
        const labelText = this.normalize(wrapper?.querySelector('legend, label, .form__label')?.textContent);
        const looksLikeColor = this.colorOptionNames.some((optionName) =>
          name.includes(optionName) || id.includes(optionName) || aria.includes(optionName) || labelText.includes(optionName)
        );
        if (!looksLikeColor) return;

        if (control.tagName === 'SELECT') {
          Array.from(control.options).forEach((option) => {
            if (this.normalize(option.value) === target) {
              control.value = option.value;
              control.dispatchEvent(new Event('input', { bubbles: true }));
              control.dispatchEvent(new Event('change', { bubbles: true }));
              changed = true;
            }
          });
        }

        if (control.matches('input[type="radio"]') && this.normalize(control.value) === target) {
          control.checked = true;
          control.dispatchEvent(new Event('input', { bubbles: true }));
          control.dispatchEvent(new Event('change', { bubbles: true }));
          changed = true;
        }
      });

      if (changed) {
        document.dispatchEvent(new CustomEvent('pb:color-selected-from-url', { detail: { color: requestedColor } }));
      }
      return changed;
    }

    getMainMediaElements() {
      return this.uniqueElements([
        'media-gallery .product__media-item[data-media-id]',
        '.product__media-list .product__media-item[data-media-id]',
        '.product__media-item[data-media-id]'
      ]);
    }

    getThumbnailElements() {
      return this.uniqueElements(['.thumbnail-list__item', 'media-gallery .thumbnail-list__item']);
    }

    uniqueElements(selectors) {
      const elements = [];
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (!elements.includes(el)) elements.push(el);
        });
      });
      return elements;
    }

    getMediaAlt(element) {
      const img = element.querySelector('img');
      return img?.getAttribute('alt') || element.getAttribute('data-alt') || '';
    }

    getAltGroup(alt) {
      return this.normalize(String(alt || '').split('|')[0]);
    }

    shouldShowMedia(element, selectedColor) {
      const alt = this.getMediaAlt(element);
      if (!alt) return true;
      const group = this.getAltGroup(alt);
      if (!group) return true;
      const selected = this.normalize(selectedColor);
      if (this.globalTags.includes(group)) return true;
      return group === selected;
    }

    showOrHideElement(element, shouldShow) {
      element.hidden = !shouldShow;
      element.style.display = shouldShow ? '' : 'none';
    }

    updateGallery(colorOverride = null) {
      const selectedColor = colorOverride || this.getSelectedColor();
      if (!selectedColor) return;
      const mainMediaElements = this.getMainMediaElements();
      const thumbnailElements = this.getThumbnailElements();
      if (!mainMediaElements.length && !thumbnailElements.length) return;
      let firstVisibleMainMedia = null;

      mainMediaElements.forEach((element) => {
        const shouldShow = this.shouldShowMedia(element, selectedColor);
        this.showOrHideElement(element, shouldShow);
        if (shouldShow && !firstVisibleMainMedia) firstVisibleMainMedia = element;
      });
      thumbnailElements.forEach((element) => this.showOrHideElement(element, this.shouldShowMedia(element, selectedColor)));
      window.requestAnimationFrame(() => this.activateFirstVisibleMedia(firstVisibleMainMedia));
    }

    activateFirstVisibleMedia(firstVisibleMainMedia) {
      if (!firstVisibleMainMedia) return;
      const mediaGallery = document.querySelector('media-gallery');
      const mediaId = firstVisibleMainMedia.getAttribute('data-media-id');
      if (mediaGallery && mediaId && typeof mediaGallery.setActiveMedia === 'function') {
        const currentActiveMedia = mediaGallery.querySelector('.product__media-item.is-active[data-media-id]');
        const currentMediaId = currentActiveMedia?.dataset.mediaId;
        if (currentMediaId !== mediaId) mediaGallery.setActiveMedia(mediaId, true);
        return;
      }
      const visibleThumbButton = Array.from(document.querySelectorAll('.thumbnail-list__item button')).find((button) => {
        const item = button.closest('.thumbnail-list__item');
        return item && item.style.display !== 'none' && !item.hidden;
      });
      if (visibleThumbButton) visibleThumbButton.click();
    }

    run() {
      const keepsakeOwnsGallery = !!document.querySelector('[data-pb-kc][data-pb-kc-ready="true"]');
      if (!keepsakeOwnsGallery && this.pendingSelectFromUrl) this.selectColorFromUrl();
      this.pendingSelectFromUrl = false;
      if (!keepsakeOwnsGallery) this.updateGallery(this.pendingColor);
      this.pendingColor = null;
      hidePersonalizationPicker();
      hideNativeAddToCartWhenCustomizerExists();
      installKeepsakeCustomBoxLinks();
    }

    schedule(options = {}) {
      if (options.color) this.pendingColor = options.color;
      if (options.selectFromUrl) this.pendingSelectFromUrl = true;
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.run();
      });
    }

    bindEvents() {
      document.addEventListener('change', (event) => {
        if (!event.target.matches('variant-selects select, variant-radios input[type="radio"], .product-form__input select, .product-form__input input[type="radio"]')) return;
        this.schedule();
      });

      document.addEventListener('click', (event) => {
        const color = event.target.closest('[data-pb-kc-colors] [data-color]')?.dataset.color;
        if (color) this.schedule({ color });
      });

      document.addEventListener('variant:change', () => this.schedule());
      document.addEventListener('pb:keepsake:statechange', (event) => this.schedule({ color: event.detail?.color }));
      document.addEventListener('shopify:section:load', () => this.schedule({ selectFromUrl: true }));
    }

    init() {
      this.bindEvents();
      const run = () => this.schedule({ selectFromUrl: true });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
      else run();
    }
  }

  function hidePersonalizationPicker() {
    const labels = Array.from(document.querySelectorAll('.product-form__input legend, .product-form__input label, .form__label'));
    labels.forEach((label) => {
      const text = String(label.textContent || '').trim().toLowerCase();
      if (!text.includes('personalization')) return;
      const wrapper = label.closest('.product-form__input, fieldset');
      if (wrapper) {
        wrapper.style.display = 'none';
        wrapper.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function hideNativeAddToCartWhenCustomizerExists() {
    const customizerButton = document.querySelector('[data-pb-customizer-open], [data-pb-standalone-usb-open], .pb-photo-customizer-open, .pb-customizer-open');
    if (!customizerButton) return;
    const productInfo = customizerButton.closest('.product__info-container, .product, .product-form') || document;
    const nativeButtons = productInfo.querySelectorAll('product-form button[name="add"], form[action*="/cart/add"] button[name="add"], .product-form__submit');
    nativeButtons.forEach((button) => {
      if (button === customizerButton || button.contains(customizerButton)) return;
      const wrapper = button.closest('product-form, .product-form, form[action*="/cart/add"]');
      if (wrapper) {
        wrapper.style.display = 'none';
        wrapper.setAttribute('aria-hidden', 'true');
      } else {
        button.style.display = 'none';
        button.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function installKeepsakeCustomBoxLinks() {
    if (!document.getElementById('PBKeepsakeCustomBoxLinkStyles')) {
      const style = document.createElement('style');
      style.id = 'PBKeepsakeCustomBoxLinkStyles';
      style.textContent = '.pb-kc__custom-box-link{margin:.9rem 0 0;font-size:1.2rem;line-height:1.45;text-align:center;color:rgba(42,35,28,.68)}.pb-kc__custom-box-link a{color:inherit;text-decoration:underline;text-underline-offset:.2em;text-decoration-thickness:.08em}.pb-kc__custom-box-link a:hover{color:rgba(42,35,28,.92)}';
      document.head.append(style);
    }
    document.querySelectorAll('[data-pb-kc] .pb-kc__assurance').forEach((assurance) => {
      if (assurance.parentElement?.querySelector('.pb-kc__custom-box-link')) return;
      const note = document.createElement('div');
      note.className = 'pb-kc__custom-box-link';
      note.innerHTML = 'Need something more specific? <a href="/pages/custom-boxes">Explore Custom Boxes →</a>';
      assurance.insertAdjacentElement('afterend', note);
    });
  }

  installKeepsakeCustomBoxLinks();
  new PBVariantGallery();
})();
